import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, CurrencyInr, PencilSimple, Trash, FileText, Link as LinkIcon, Download } from '@phosphor-icons/react';
import { GeneratePaymentLinkDialog } from '../components/admin/GeneratePaymentLinkDialog';
import { formatAnyDate } from '../utils/dateFormat';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

export function CustomerDetail() {
  const { mobile } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [tab, setTab] = useState('profile');
  const [loading, setLoading] = useState(true);

  // Profile edit
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', address: '', executive_name: '' });
  const [executives, setExecutives] = useState([]);

  // Payment
  const [showPay, setShowPay] = useState(false);
  const [payForm, setPayForm] = useState({
    amount: '', payment_date: today(), mode: 'Cash', reference: '',
    jo_number: '', quotation_no: '', remarks: ''
  });

  // Order value
  const [editValueJo, setEditValueJo] = useState(null);
  const [orderValueInput, setOrderValueInput] = useState('');

  // Mobile linkage tabs (lazy loaded)
  const [mobileQuotations, setMobileQuotations] = useState(null);
  const [mobileJobOrders, setMobileJobOrders] = useState(null);
  const [waLog, setWaLog] = useState(null);
  const [paymentLinks, setPaymentLinks] = useState(null);
  const [showPL, setShowPL] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [det, led] = await Promise.all([
        api.get(`/api/admin/customers/${mobile}`),
        api.get(`/api/admin/customers/${mobile}/ledger`),
      ]);
      setData(det.data);
      setLedger(led.data);
      setProfileForm({
        name: det.data.customer.name || '',
        address: det.data.customer.address || '',
        executive_name: det.data.customer.executive_name || ''
      });
    } catch (e) {
      toast.error('Failed to load customer');
    } finally {
      setLoading(false);
    }
  }, [mobile]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/api/masters/executives').then(r => setExecutives(r.data || [])).catch(() => {});
  }, []);

  // Lazy-load mobile tabs on selection
  useEffect(() => {
    if (tab === 'mobile-quotations' && mobileQuotations === null) {
      api.get(`/api/admin/customers/${mobile}/mobile-quotations`).then(r => setMobileQuotations(r.data.quotations || [])).catch(() => setMobileQuotations([]));
    } else if (tab === 'mobile-job-orders' && mobileJobOrders === null) {
      api.get(`/api/admin/customers/${mobile}/mobile-job-orders`).then(r => setMobileJobOrders(r.data.job_orders || [])).catch(() => setMobileJobOrders([]));
    } else if (tab === 'whatsapp' && waLog === null) {
      api.get(`/api/admin/customers/${mobile}/notifications`).then(r => setWaLog(r.data.notifications || [])).catch(() => setWaLog([]));
    } else if (tab === 'payment-links' && paymentLinks === null) {
      api.get(`/api/admin/customers/${mobile}/payment-links`).then(r => setPaymentLinks(r.data.payment_links || [])).catch(() => setPaymentLinks([]));
    }
  }, [tab, mobile, mobileQuotations, mobileJobOrders, waLog, paymentLinks]);

  const saveProfile = async () => {
    try {
      await api.put(`/api/admin/customers/${mobile}`, profileForm);
      toast.success('Profile updated');
      setEditProfile(false);
      load();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const savePayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) {
      toast.error('Enter a valid amount'); return;
    }
    try {
      await api.post(`/api/admin/customers/${mobile}/payments`, {
        ...payForm,
        amount: Number(payForm.amount)
      });
      toast.success('Payment recorded');
      setShowPay(false);
      setPayForm({ amount: '', payment_date: today(), mode: 'Cash', reference: '', jo_number: '', quotation_no: '', remarks: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record');
    }
  };

  const deletePayment = async (pid) => {
    if (!window.confirm('Delete this payment entry?')) return;
    try {
      await api.delete(`/api/admin/customers/${mobile}/payments/${pid}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  const saveOrderValue = async (jo_number) => {
    const val = Number(orderValueInput);
    if (isNaN(val) || val < 0) { toast.error('Invalid amount'); return; }
    try {
      await api.put(`/api/admin/job-orders/${jo_number}/order-value`, { order_value: val });
      toast.success('Order value updated');
      setEditValueJo(null);
      setOrderValueInput('');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  if (loading || !data) {
    return <Layout><div className="text-center py-12">Loading...</div></Layout>;
  }

  const { customer, job_orders: jos, payments, summary } = data;

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex items-center gap-3">
          <Button data-testid="customer-back-btn" variant="ghost" onClick={() => navigate('/customers')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-3xl font-light tracking-tight text-zinc-950">{customer.name || 'Unnamed'}</h1>
            <p className="text-sm text-zinc-500 font-mono">{customer.mobile}</p>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-zinc-300 rounded-sm">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-zinc-500">Job Orders</p>
              <p className="text-2xl font-bold text-zinc-950 mt-1">{summary.jo_count}</p>
            </CardContent>
          </Card>
          <Card className="border-zinc-300 rounded-sm">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-zinc-500">Order Value</p>
              <p className="text-2xl font-bold text-zinc-950 mt-1">{fmtINR(summary.total_order_value)}</p>
            </CardContent>
          </Card>
          <Card className="border-zinc-300 rounded-sm">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-zinc-500">Paid</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">{fmtINR(summary.total_paid)}</p>
            </CardContent>
          </Card>
          <Card className={`border-zinc-300 rounded-sm ${summary.outstanding > 0 ? 'bg-red-50' : ''}`}>
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-zinc-500">Outstanding</p>
              <p className={`text-2xl font-bold mt-1 ${summary.outstanding > 0 ? 'text-red-600' : 'text-zinc-500'}`}>
                {fmtINR(summary.outstanding)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div data-testid="customer-tabs" className="flex flex-wrap gap-2 border-b border-zinc-200">
          {[
            { k: 'profile', label: 'Profile' },
            { k: 'jos', label: `Job Orders (${jos.length})` },
            { k: 'ledger', label: 'Ledger' },
            { k: 'payments', label: `Payments (${payments.length})` },
            { k: 'mobile-quotations', label: 'Mobile Quotations' },
            { k: 'mobile-job-orders', label: 'Mobile Job Orders' },
            { k: 'whatsapp', label: 'WhatsApp Log' },
            { k: 'payment-links', label: 'Payment Links' },
          ].map(t => (
            <button
              key={t.k}
              data-testid={`cust-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
                tab === t.k ? 'border-orange-500 text-orange-600' : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
            >{t.label}</button>
          ))}
          <div className="ml-auto flex gap-2 items-center py-2">
            <Button
              data-testid="export-ledger-btn"
              variant="outline"
              size="sm"
              onClick={() => window.open(`/api/admin/customers/${mobile}/ledger/export`, '_blank')}
            >
              <Download size={14} className="mr-1" /> Ledger CSV
            </Button>
            <Button
              data-testid="customer-gen-payment-link"
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => setShowPL(true)}
            >
              <LinkIcon size={14} className="mr-1" /> Payment Link
            </Button>
          </div>
        </div>

        {tab === 'profile' && (
          <Card className="border-zinc-300 rounded-sm">
            <CardHeader className="border-b border-zinc-300 flex flex-row justify-between items-center">
              <CardTitle className="text-lg">Customer Profile</CardTitle>
              {!editProfile && (
                <Button data-testid="edit-profile-btn" variant="outline" size="sm" onClick={() => setEditProfile(true)}>
                  <PencilSimple size={16} className="mr-1" /> Edit
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {editProfile ? (
                <>
                  <div>
                    <Label>Name</Label>
                    <Input data-testid="profile-name" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input data-testid="profile-address" value={profileForm.address} onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })} />
                  </div>
                  <div>
                    <Label>Executive</Label>
                    <select
                      data-testid="profile-exec"
                      className="w-full h-10 px-3 border border-zinc-300 rounded-sm bg-white"
                      value={profileForm.executive_name}
                      onChange={(e) => setProfileForm({ ...profileForm, executive_name: e.target.value })}
                    >
                      <option value="">Select Executive</option>
                      {executives.map(x => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid="save-profile-btn" onClick={saveProfile} className="bg-orange-500 hover:bg-orange-600 text-white">Save</Button>
                    <Button variant="outline" onClick={() => { setEditProfile(false); setProfileForm({ name: customer.name, address: customer.address, executive_name: customer.executive_name }); }}>Cancel</Button>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><Label className="text-xs uppercase text-zinc-500">Mobile</Label><p className="font-mono text-zinc-950">{customer.mobile}</p></div>
                  <div><Label className="text-xs uppercase text-zinc-500">Name</Label><p className="text-zinc-950">{customer.name || '—'}</p></div>
                  <div className="md:col-span-2"><Label className="text-xs uppercase text-zinc-500">Address</Label><p className="text-zinc-950">{customer.address || '—'}</p></div>
                  <div><Label className="text-xs uppercase text-zinc-500">Executive</Label><p className="text-zinc-950">{customer.executive_name || '—'}</p></div>
                  <div><Label className="text-xs uppercase text-zinc-500">Source</Label><p className="text-zinc-600 text-xs">{customer.source}</p></div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'jos' && (
          <Card className="border-zinc-300 rounded-sm">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-300">
                  <tr>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">JO #</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Date</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Status</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Executive</th>
                    <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Order Value</th>
                    <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {jos.length === 0 ? (
                    <tr><td colSpan={6} className="p-6 text-center text-zinc-400">No job orders linked</td></tr>
                  ) : jos.map(jo => (
                    <tr key={jo.jo_number} className="border-b border-zinc-200">
                      <td className="p-3 font-mono">
                        <Link to={`/job-orders/${jo.jo_number}`} className="text-blue-600 hover:underline" data-testid={`jo-link-${jo.jo_number}`}>{jo.jo_number}</Link>
                      </td>
                      <td className="p-3">{formatAnyDate(jo.jo_date)}</td>
                      <td className="p-3"><Badge variant="outline" className="text-xs">{jo.status}</Badge></td>
                      <td className="p-3">{jo.executive_name}</td>
                      <td className="p-3 text-right">
                        {editValueJo === jo.jo_number ? (
                          <div className="flex gap-1 justify-end items-center">
                            <Input
                              data-testid={`ov-input-${jo.jo_number}`}
                              type="number"
                              value={orderValueInput}
                              onChange={(e) => setOrderValueInput(e.target.value)}
                              className="h-8 w-32 text-right"
                            />
                            <Button data-testid={`ov-save-${jo.jo_number}`} size="sm" onClick={() => saveOrderValue(jo.jo_number)} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white">Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditValueJo(null); setOrderValueInput(''); }} className="h-8">×</Button>
                          </div>
                        ) : (
                          <span className="font-bold">{fmtINR(jo.order_value)}</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {editValueJo !== jo.jo_number && (
                          <Button data-testid={`ov-edit-${jo.jo_number}`} variant="ghost" size="sm" onClick={() => { setEditValueJo(jo.jo_number); setOrderValueInput(String(jo.order_value || 0)); }}>
                            <PencilSimple size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {tab === 'ledger' && ledger && (
          <Card className="border-zinc-300 rounded-sm">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-300">
                  <tr>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Date</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Description</th>
                    <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Debit</th>
                    <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Credit</th>
                    <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.rows.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-zinc-400">No transactions</td></tr>
                  ) : ledger.rows.map((r, i) => (
                    <tr key={i} className={`border-b border-zinc-200 ${r.type === 'payment' ? 'bg-emerald-50/40' : ''}`}>
                      <td className="p-3 font-mono text-xs">{r.date}</td>
                      <td className="p-3">{r.description}{r.remarks ? <span className="text-xs text-zinc-500 block">{r.remarks}</span> : null}</td>
                      <td className="p-3 text-right text-zinc-950">{r.debit > 0 ? fmtINR(r.debit) : ''}</td>
                      <td className="p-3 text-right text-emerald-700">{r.credit > 0 ? fmtINR(r.credit) : ''}</td>
                      <td className={`p-3 text-right font-bold ${r.balance > 0 ? 'text-red-600' : 'text-zinc-500'}`}>{fmtINR(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
                {ledger.rows.length > 0 && (
                  <tfoot className="bg-zinc-100 font-bold">
                    <tr>
                      <td colSpan={2} className="p-3 text-right">Total</td>
                      <td className="p-3 text-right">{fmtINR(ledger.summary.total_order_value)}</td>
                      <td className="p-3 text-right text-emerald-700">{fmtINR(ledger.summary.total_paid)}</td>
                      <td className={`p-3 text-right ${ledger.summary.outstanding > 0 ? 'text-red-600' : 'text-zinc-500'}`}>{fmtINR(ledger.summary.outstanding)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </CardContent>
          </Card>
        )}

        {tab === 'payments' && (
          <Card className="border-zinc-300 rounded-sm">
            <CardHeader className="border-b border-zinc-300 flex flex-row justify-between items-center">
              <CardTitle className="text-lg">Payments</CardTitle>
              <Button data-testid="new-payment-btn" onClick={() => setShowPay(true)} className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus size={16} className="mr-1" /> Record Payment
              </Button>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-300">
                  <tr>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Date</th>
                    <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Amount</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Mode</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Reference</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">JO #</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Remarks</th>
                    <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Source</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr><td colSpan={8} className="p-6 text-center text-zinc-400">No payments yet</td></tr>
                  ) : payments.map(p => (
                    <tr key={p._id} className="border-b border-zinc-200">
                      <td className="p-3 font-mono text-xs">{p.payment_date}</td>
                      <td className="p-3 text-right font-bold text-emerald-700">{fmtINR(p.amount)}</td>
                      <td className="p-3">{p.mode}</td>
                      <td className="p-3 font-mono text-xs">{p.reference || '—'}</td>
                      <td className="p-3 font-mono text-xs">{p.jo_number || '—'}</td>
                      <td className="p-3 text-xs">{p.remarks || '—'}</td>
                      <td className="p-3 text-center"><Badge variant="outline" className="text-xs">{p.source}</Badge></td>
                      <td className="p-3 text-right">
                        <Button data-testid={`del-pay-${p._id}`} variant="ghost" size="sm" onClick={() => deletePayment(p._id)}>
                          <Trash size={14} className="text-red-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
        {tab === 'mobile-quotations' && (
          <Card className="border-zinc-300 rounded-sm">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-300">
                  <tr>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Quotation #</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Date</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Executive</th>
                    <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Items</th>
                    <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Amount</th>
                    <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Status</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {mobileQuotations === null ? (
                    <tr><td colSpan={7} className="p-6 text-center text-zinc-400">Loading...</td></tr>
                  ) : mobileQuotations.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No mobile quotations</td></tr>
                  ) : mobileQuotations.map((q) => (
                    <tr key={q.quotation_number} className="border-b border-zinc-200">
                      <td className="p-3 font-mono">
                        <Link to={`/admin/mobile-quotations/${q.quotation_number}`} className="text-blue-600 hover:underline">{q.quotation_number}</Link>
                      </td>
                      <td className="p-3 text-xs">{q.quotation_date}</td>
                      <td className="p-3 text-xs">{q.executive_name}</td>
                      <td className="p-3 text-center">{q.item_count}</td>
                      <td className="p-3 text-right font-bold">{fmtINR(q.total_amount)}</td>
                      <td className="p-3 text-center"><Badge variant="outline" className="text-xs">{q.status}</Badge></td>
                      <td className="p-3 text-right">
                        <a href={`/api/admin/mobile-quotations/${q.quotation_number}/pdf`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm"><FileText size={14} /></Button>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {tab === 'mobile-job-orders' && (
          <Card className="border-zinc-300 rounded-sm">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-300">
                  <tr>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">JO #</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Date</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Executive</th>
                    <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Order Value</th>
                    <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Status</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {mobileJobOrders === null ? (
                    <tr><td colSpan={6} className="p-6 text-center text-zinc-400">Loading...</td></tr>
                  ) : mobileJobOrders.length === 0 ? (
                    <tr><td colSpan={6} className="p-6 text-center text-zinc-400">No mobile job orders</td></tr>
                  ) : mobileJobOrders.map((j) => (
                    <tr key={j.jo_number} className="border-b border-zinc-200">
                      <td className="p-3 font-mono">
                        <Link to={`/admin/mobile-job-orders/${j.jo_number}`} className="text-blue-600 hover:underline">{j.jo_number}</Link>
                      </td>
                      <td className="p-3 text-xs">{formatAnyDate(j.jo_date)}</td>
                      <td className="p-3 text-xs">{j.executive_name}</td>
                      <td className="p-3 text-right font-bold">{fmtINR(j.order_value || j.total_amount)}</td>
                      <td className="p-3 text-center"><Badge variant="outline" className="text-xs">{j.status}</Badge></td>
                      <td className="p-3 text-right">
                        <a href={`/api/admin/mobile-job-orders/${j.jo_number}/pdf`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="sm"><FileText size={14} /></Button>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {tab === 'whatsapp' && (
          <Card className="border-zinc-300 rounded-sm">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-300">
                  <tr>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">When</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Event</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">Body</th>
                    <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Status</th>
                    <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {waLog === null ? (
                    <tr><td colSpan={5} className="p-6 text-center text-zinc-400">Loading...</td></tr>
                  ) : waLog.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-zinc-400">No messages</td></tr>
                  ) : waLog.map((n) => (
                    <tr key={n._id} className="border-b border-zinc-200">
                      <td className="p-3 text-xs">{n.created_at ? new Date(n.created_at).toLocaleString() : '—'}</td>
                      <td className="p-3 font-mono text-xs">{n.event_key}</td>
                      <td className="p-3 text-xs max-w-md truncate" title={n.body}>{n.body}</td>
                      <td className="p-3 text-center"><Badge variant="outline" className="text-[10px] uppercase">{n.status}</Badge></td>
                      <td className="p-3 text-center"><Badge variant="outline" className="text-[10px] uppercase">{n.source}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {tab === 'payment-links' && (
          <Card className="border-zinc-300 rounded-sm">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-300">
                  <tr>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">When</th>
                    <th className="text-left p-3 font-bold uppercase text-xs text-zinc-700">JO #</th>
                    <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Amount</th>
                    <th className="text-right p-3 font-bold uppercase text-xs text-zinc-700">Paid</th>
                    <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Status</th>
                    <th className="text-center p-3 font-bold uppercase text-xs text-zinc-700">Source</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {paymentLinks === null ? (
                    <tr><td colSpan={7} className="p-6 text-center text-zinc-400">Loading...</td></tr>
                  ) : paymentLinks.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No payment links yet</td></tr>
                  ) : paymentLinks.map((pl) => (
                    <tr key={pl._id} className="border-b border-zinc-200">
                      <td className="p-3 text-xs">{pl.created_at ? new Date(pl.created_at).toLocaleString() : '—'}</td>
                      <td className="p-3 font-mono text-xs">{pl.jo_number || pl.quotation_number || '—'}</td>
                      <td className="p-3 text-right">{fmtINR(pl.amount)}</td>
                      <td className="p-3 text-right text-emerald-700">{fmtINR(pl.paid_amount)}</td>
                      <td className="p-3 text-center"><Badge variant="outline" className="text-[10px] uppercase">{pl.status}</Badge></td>
                      <td className="p-3 text-center"><Badge variant="outline" className="text-[10px] uppercase">{pl.source}</Badge></td>
                      <td className="p-3 text-right">
                        {pl.link_url && (
                          <a href={pl.link_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">Open</a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      <GeneratePaymentLinkDialog
        open={showPL}
        onOpenChange={setShowPL}
        prefill={{
          customer_name: customer.name,
          customer_mobile: customer.mobile,
          amount: Math.max(0, summary.outstanding || 0),
          description: `Payment from ${customer.name || customer.mobile}`,
        }}
        onCreated={() => { setPaymentLinks(null); setTab('payment-links'); }}
      />
      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount *</Label>
                <Input data-testid="pay-amount" type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
              </div>
              <div>
                <Label>Date</Label>
                <Input data-testid="pay-date" type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Mode</Label>
              <select
                data-testid="pay-mode"
                className="w-full h-10 px-3 border border-zinc-300 rounded-sm bg-white"
                value={payForm.mode}
                onChange={(e) => setPayForm({ ...payForm, mode: e.target.value })}
              >
                {['Cash', 'UPI', 'Bank Transfer', 'Cheque', 'Razorpay', 'Other'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label>Reference (Txn ID / Cheque No)</Label>
              <Input data-testid="pay-ref" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>JO Number (optional)</Label>
                <Input data-testid="pay-jo" value={payForm.jo_number} onChange={(e) => setPayForm({ ...payForm, jo_number: e.target.value })} />
              </div>
              <div>
                <Label>Quotation No (optional)</Label>
                <Input data-testid="pay-quot" value={payForm.quotation_no} onChange={(e) => setPayForm({ ...payForm, quotation_no: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Remarks</Label>
              <Input data-testid="pay-remarks" value={payForm.remarks} onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(false)}>Cancel</Button>
            <Button data-testid="save-payment-btn" onClick={savePayment} className="bg-orange-500 hover:bg-orange-600 text-white">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
